import React from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Image,
  ScrollView,
} from "react-native";
import LHOverlay from "../../../components/LHOverlay";
import { PieChart } from "react-native-chart-kit";
import { Dimensions } from "react-native";
import { useTheme } from "../../../theme";

interface TouchpointDetailsOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  touchpoint: any | null;
}

const TouchpointDetailsOverlay: React.FC<TouchpointDetailsOverlayProps> = ({
  visible,
  onDismiss,
  touchpoint,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  if (!touchpoint) {
    return null;
  }

  console.log("Touchpoint data received in overlay:", touchpoint); // Debugging log

  const renderProbationOverlay = () => {
    // Calculate the counts for Yes, No, and N/A responses
    const responseCounts = touchpoint.data.reduce(
      (acc: { yes: number; no: number; na: number }, item: any) => {
        if (item.Response === 1) {
          acc.yes += 1;
        } else if (item.Response === 2) {
          acc.no += 1;
        } else if (item.Response === 3) {
          acc.na += 1;
        }
        return acc;
      },
      { yes: 0, no: 0, na: 0 }
    );

    const pieData = [
      {
        name: "Yes",
        population: responseCounts.yes,
        color: "#4caf50",
        legendFontColor: isDarkTheme ? colors.text.primary : "#7F7F7F",
        legendFontSize: 14,
      },
      {
        name: "No",
        population: responseCounts.no,
        color: "#f44336",
        legendFontColor: isDarkTheme ? colors.text.primary : "#7F7F7F",
        legendFontSize: 14,
      },
      {
        name: "N/A",
        population: responseCounts.na,
        color: "#9e9e9e",
        legendFontColor: isDarkTheme ? colors.text.primary : "#7F7F7F",
        legendFontSize: 14,
      },
    ].filter((item) => item.population > 0); // Only show items with data

    const screenWidth = Dimensions.get("window").width;
    const chartConfig = {
      backgroundGradientFrom: isDarkTheme
        ? colors.background.surface
        : "#fff",
      backgroundGradientTo: isDarkTheme
        ? colors.background.surface
        : "#fff",
      color: (opacity = 1) =>
        isDarkTheme
          ? `rgba(255, 255, 255, ${opacity})`
          : `rgba(0, 0, 0, ${opacity})`,
      strokeWidth: 2,
      barPercentage: 0.5,
      useShadowColorFromDataset: false,
    };

    console.log("Employee Signature:", touchpoint.probation.employee_signature);
    console.log("Manager Signature:", touchpoint.probation.manager_signature);

    return (
      <View style={[styles.overlayContent, isDarkTheme && { backgroundColor: colors.background.surface }]}>
        <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
          <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }] }>
            {touchpoint.type} - Comments 1
          </Text>
          <Text style={[styles.overlayAdditionalContent, isDarkTheme && { color: colors.text.primary }]}>
            {touchpoint.probation.observations}
          </Text>
        </View>
        <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
          <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }] }>
            {touchpoint.type} - Comments 2
          </Text>
          <Text style={[styles.overlayAdditionalContent, isDarkTheme && { color: colors.text.primary }]}>
            {touchpoint.probation.additional_observations}
          </Text>
        </View>

        {/* Pie Chart Section */}
        <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
          <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }]}>Response Distribution</Text>
          {pieData.length > 0 ? (
            <View style={styles.chartContainer}>
              <PieChart
                data={pieData}
                width={screenWidth - 100}
                height={220}
                chartConfig={chartConfig}
                accessor="population"
                backgroundColor="transparent"
                paddingLeft="15"
                center={[10, 10]}
                absolute={false}
              />
            </View>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={[styles.noDataText, isDarkTheme && { color: colors.text.secondary }]}>No response data available</Text>
            </View>
          )}
        </View>

        <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
          <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }]}>Employee Signature</Text>
          <View style={[styles.signatureContainer, isDarkTheme && { borderTopColor: colors.border.default, backgroundColor: colors.background.surface }]}>
            {renderSignature(touchpoint.probation.employee_signature)}
          </View>
        </View>

        <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
          <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }]}>Manager Signature</Text>
          <View style={[styles.signatureContainer, isDarkTheme && { borderTopColor: colors.border.default, backgroundColor: colors.background.surface }]}>
            {renderSignature(touchpoint.probation.manager_signature)}
          </View>
        </View>
      </View>
    );
  };

  const renderMetricsTable = () => (
    <View style={[styles.overlayContent, isDarkTheme && { backgroundColor: colors.background.surface }] }>
      <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
        <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }]}>Metrics Table</Text>
        <ScrollView
          horizontal={true}
          showsHorizontalScrollIndicator={Platform.OS === "ios"}
          style={
            Platform.OS === "ios" ? styles.horizontalScrollView : undefined
          }
          contentContainerStyle={
            Platform.OS === "ios" ? styles.scrollContent : undefined
          }
        >
          <View
            style={[
              styles.tableContainer,
              Platform.OS === "ios" && styles.iosTableContainer,
            ]}
          >
            <View style={[styles.tableRowHeader, isDarkTheme && { backgroundColor: colors.background.surface }] }>
              <Text style={[styles.tableCellHeader, isDarkTheme && { color: colors.text.primary }]}>Week</Text>
              <Text style={styles.tableCellHeader}>DPS Goal</Text>
              <Text style={styles.tableCellHeader}>DPS Actual</Text>
              <Text style={styles.tableCellHeader}>CPH Goal</Text>
              <Text style={styles.tableCellHeader}>CPH Actual</Text>
              <Text style={styles.tableCellHeader}>Gap Time Goal</Text>
              <Text style={styles.tableCellHeader}>Gap Time Actual</Text>
              <Text style={styles.tableCellHeader}>SIA Goal</Text>
              <Text style={styles.tableCellHeader}>SIA Actual</Text>
              <Text style={styles.tableCellHeader}>Period</Text>
              <Text style={[styles.tableCellHeader, isDarkTheme && { color: colors.text.primary }]}> Week</Text>
            </View>
            {touchpoint.metrics.map((metric: any, index: number) => (
              <View key={index} style={[styles.tableRow, isDarkTheme && { borderBottomColor: colors.border.divider }] }>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.Week}</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.DPS_Target}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.DPS_Actual}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.CPH_Target}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.CPH_Actual}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.Gap_Time_Target}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.Gap_Time_Actual}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.SIA_Target}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.SIA_Actual}%</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.Period}</Text>
                <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{metric.Pepsi_Week}</Text>
              </View>
            ))}
          </View>
        </ScrollView>
      </View>

      <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
        <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }]}>Attendance Table</Text>
        <View style={styles.tableContainer}>
          <View style={[styles.tableRowHeader, isDarkTheme && { backgroundColor: colors.background.surface }] }>
            <Text style={[styles.tableCellHeader, isDarkTheme && { color: colors.text.primary }]}>S.No</Text>
            <Text style={[styles.tableCellHeader, isDarkTheme && { color: colors.text.primary }]}>Date of Absence</Text>
            <Text style={[styles.tableCellHeader, isDarkTheme && { color: colors.text.primary }]}>Points</Text>
          </View>
          {touchpoint.attendance.map((attendance: any, index: number) => (
            <View key={index} style={[styles.tableRow, isDarkTheme && { borderBottomColor: colors.border.divider }] }>
              <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{index + 1}</Text>
              <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{attendance.Date_of_Absence}</Text>
              <Text style={[styles.tableCell, isDarkTheme && { color: colors.text.primary }]}>{attendance.Points}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
        <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }] }>
          Pulse Check - Observation
        </Text>
        <Text style={[styles.overlayAdditionalContent, isDarkTheme && { color: colors.text.primary }] }>
          {touchpoint.observations}
        </Text>
      </View>

      <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
        <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }]}>Employee Signature</Text>
        <View style={[styles.signatureContainer, isDarkTheme && { borderTopColor: colors.border.default, backgroundColor: colors.background.surface }]}>
          {renderSignature(touchpoint.employee_signature)}
        </View>
      </View>

      <View style={[styles.overlaySection, isDarkTheme && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }] }>
        <Text style={[styles.overlaySectionTitle, isDarkTheme && { backgroundColor: colors.brand.primary, color: colors.text.primary }]}>Manager Signature</Text>
        <View style={[styles.signatureContainer, isDarkTheme && { borderTopColor: colors.border.default, backgroundColor: colors.background.surface }]}>
          {renderSignature(touchpoint.manager_signature)}
        </View>
      </View>
    </View>
  );

  const renderSignature = (signature: any) => {
    console.log("Rendering signature:", signature);
    if (signature) {
      try {
        const isValidUrl =
          typeof signature === "string" && signature.startsWith("http");
        const isBase64 =
          typeof signature === "string" && signature.startsWith("data:image");

        if (isValidUrl) {
          console.log("Valid signature URL:", signature);
          return (
            <Image
              source={{ uri: signature }}
              style={[styles.signatureImage, { width: 100, height: 100 }]}
              resizeMode="contain"
              onError={(error) => {
                console.error(
                  "Failed to load signature. Displaying placeholder.",
                  error.nativeEvent
                );
              }}
            />
          );
        } else if (isBase64) {
          console.log("Rendering base64 signature.");
          return (
            <Image
              source={{ uri: signature }}
              style={[styles.signatureImage, { width: 100, height: 100 }]}
              resizeMode="contain"
            />
          );
        } else {
          console.warn("Invalid signature format:", signature);
        }
      } catch (error) {
        console.error("Error rendering signature:", error);
      }
    }

    // Fallback placeholder wrapped in a proper container to avoid unexpected text node errors
    return (
      <View style={styles.signaturePlaceholderContainer}>
        <Text style={[styles.signaturePlaceholder, isDarkTheme && { color: colors.text.secondary }]}>No signature available</Text>
      </View>
    );
  };

  const renderOverlayContent = () => {
    console.log("Touchpoint type:", touchpoint.type); // Debugging log
    return (
      <>
        {touchpoint.type === "Pulse Check" && renderMetricsTable()}
        {(touchpoint.type === "Probation 30" ||
          touchpoint.type === "Probation 60" ||
          touchpoint.type === "Probation 90") &&
          renderProbationOverlay()}
      </>
    );
  };

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title="Touchpoint Details"
      size="large"
    >
      {renderOverlayContent()}
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  overlayContent: {
    padding: Platform.OS === "web" ? 16 : 12,
  },
  overlaySection: {
    marginBottom: Platform.OS === "web" ? 24 : 16,
    borderWidth: 1,
    borderColor: "#eaeaea",
    borderRadius: 8,
    overflow: "hidden",
  },
  overlaySectionTitle: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    fontWeight: "bold",
    backgroundColor: "#0066CC",
    color: "#FFFFFF",
    padding: Platform.OS === "web" ? 12 : 10,
  },
  overlaySummary: {
    padding: Platform.OS === "web" ? 16 : 12,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  overlayField: {
    width: Platform.OS === "web" ? "50%" : "100%",
    marginBottom: Platform.OS === "web" ? 12 : 8,
    paddingRight: Platform.OS === "web" ? 8 : 0,
  },
  overlayFieldLabel: {
    fontSize: Platform.OS === "web" ? 14 : 13,
    fontWeight: "bold",
    color: "#616161",
    marginBottom: 4,
  },
  overlayFieldValue: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    color: "#212121",
  },
  overlayAdditionalContent: {
    padding: Platform.OS === "web" ? 16 : 12,
    fontSize: Platform.OS === "web" ? 15 : 14,
    lineHeight: Platform.OS === "web" ? 22 : 20,
    color: "#424242",
  },
  tableContainer: {
    padding: Platform.OS === "web" ? 16 : 12,
  },
  iosTableContainer: {
    minWidth: 800, // Minimum width for horizontal scrolling on iOS
  },
  tableRowHeader: {
    flexDirection: "row",
    backgroundColor: "#f5f5f5",
    padding: 8,
  },
  tableCellHeader: {
    flex: 1,
    fontWeight: "bold",
    color: "#333",
    textAlign: "center",
    ...(Platform.OS === "ios" && { minWidth: 70 }),
  },
  tableRow: {
    flexDirection: "row",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#eaeaea",
  },
  tableCell: {
    flex: 1,
    color: "#212121",
    textAlign: "center",
    ...(Platform.OS === "ios" && { minWidth: 70 }),
  },
  signatureContainer: {
    padding: Platform.OS === "web" ? 16 : 12,
    alignItems: "center",
    justifyContent: "center",
    height: 100,
    borderTopWidth: 1,
    borderTopColor: "#eaeaea",
  },
  signaturePlaceholderContainer: {
    alignItems: "center",
    justifyContent: "center",
    height: 100,
  },
  signaturePlaceholder: {
    fontSize: Platform.OS === "web" ? 18 : 16,
    color: "#BDBDBD",
  },
  signatureImage: {
    width: "100%",
    height: "100%",
  },
  horizontalScrollView: {
    flexGrow: 1,
  },
  scrollContent: {
    minWidth: "100%",
  },
  chartContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  noDataContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
  },
  noDataText: {
    fontSize: 14,
    color: "#666",
    fontStyle: "italic",
  },
});

export default TouchpointDetailsOverlay;
